import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const TimelineEntry = ({ entry, onExpand }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    if (onExpand) onExpand(entry?.id);
  };

  const getSeverityColor = (severity) => {
    if (severity <= 3) return 'text-success bg-success/10';
    if (severity <= 6) return 'text-warning bg-warning/10';
    return 'text-error bg-error/10';
  };

  const getSeverityIcon = (severity) => {
    if (severity <= 3) return 'SmilePlus';
    if (severity <= 6) return 'Meh';
    return 'Frown';
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-therapeutic transition-smooth hover:shadow-therapeutic-md">
      <button
        onClick={handleToggle}
        className="w-full p-4 sm:p-6 text-left focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded-xl"
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} symptom entry from ${formatDate(entry?.date)}`}
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center ${getSeverityColor(entry?.severity)}`}>
            <Icon name={getSeverityIcon(entry?.severity)} size={20} strokeWidth={2} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="text-base sm:text-lg font-heading font-semibold text-foreground line-clamp-1">
                {entry?.symptomType}
              </h3>
              <Icon 
                name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                size={20} 
                className="text-muted-foreground flex-shrink-0"
                strokeWidth={2}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground mb-2">
              <span className="flex items-center gap-1">
                <Icon name="Calendar" size={14} strokeWidth={2} />
                {formatDate(entry?.date)}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Clock" size={14} strokeWidth={2} />
                {formatTime(entry?.date)}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${getSeverityColor(entry?.severity)}`}>
                Severity: {entry?.severity}/10
              </span>
              {entry?.location && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground">
                  <Icon name="MapPin" size={12} strokeWidth={2} />
                  {entry?.location}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>
      {isExpanded && (
        <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="h-px bg-border" />

          {entry?.description && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <Icon name="FileText" size={16} strokeWidth={2} />
                Description
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {entry?.description}
              </p>
            </div>
          )}

          {entry?.onset && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <Icon name="Clock" size={16} strokeWidth={2} />
                When it started
              </h4>
              <p className="text-sm text-muted-foreground">{entry?.onset}</p>
            </div>
          )}

          {entry?.triggers && entry?.triggers?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <Icon name="Zap" size={16} strokeWidth={2} />
                Triggers
              </h4>
              <div className="flex flex-wrap gap-2">
                {entry?.triggers?.map((trigger, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 rounded-lg bg-accent/10 text-accent text-xs font-medium"
                  >
                    {trigger}
                  </span>
                ))}
              </div>
            </div>
          )}

          {entry?.dailyImpact && entry?.dailyImpact?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <Icon name="Activity" size={16} strokeWidth={2} />
                Daily Impact
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {entry?.dailyImpact?.map((impact, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted text-sm"
                  >
                    <Icon name="Check" size={14} strokeWidth={2} className="text-primary" />
                    <span className="text-foreground">{impact}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {entry?.medication && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <Icon name="Pill" size={16} strokeWidth={2} />
                Medication
              </h4>
              <p className="text-sm text-muted-foreground">{entry?.medication}</p>
            </div>
          )}

          {entry?.userType && (
            <div className="pt-2 border-t border-border">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary text-xs font-medium">
                <Icon name="User" size={14} strokeWidth={2} />
                {entry?.userType}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimelineEntry;